import { Session } from "ezStore";
import moment from "moment";
import { intl } from "LocaleWrapper";
import { hasPluginPermission } from "imports/Utils";
import { Cargo } from "types/index";
import { GeneralCallFragment, CallTimesFragment } from "__generated__/view/calls/graphql/call.fragments.generated";

type CallWithTimesAndActualStatusCode = Omit<CallTimesFragment, "__typename"> & Pick<GeneralCallFragment, "actualStatusCode">;

enum CallWindowStatus {
	UNKNOWN = 0,
	FINISHED = 1,
	ON_TIME = 2,
	BARELY_ON_TIME = 3,
	NOT_ON_TIME = 4
}

enum CallPtaStatus {
	EXECUTED = "executed",
	TOO_LATE = "tooLate",
	TOO_EARLY = "tooEarly",
	ON_TIME = "onTime",
	IN_PROGRESS = "inProgress",
	UNKNOWN = "unknown"
}

export enum CallStatusColor {
	GRAY = "#808080",
	BLUE = "#3190D8",
	RED = "#D23838",
	ORANGE = "#D18427",
	GREEN = "#45B741",
	PURPLE = "#A17891"
}

export function calculateCallStatus(call: CallWithTimesAndActualStatusCode | undefined | null, session: Session) {
	if (hasPluginPermission(session, "useContainers")) {
		return getCallPtaStatus(call);
	} else {
		return getCallWindowStatus(call);
	}
}

export function calculateMultipleCallStatus(calls: CallWithTimesAndActualStatusCode[], session: Session) {
	if (hasPluginPermission(session, "useContainers")) {
		const isTooLate = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.TOO_LATE);
		const isTooEarly = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.TOO_EARLY);
		const isOnTime = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.ON_TIME);
		const isInProgress = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.IN_PROGRESS);
		const isExecuted = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.EXECUTED);
		const isUnknown = calls.find(call => getCallPtaStatus(call).type === CallPtaStatus.UNKNOWN);

		if (isTooLate) {
			return getCallPtaStatus(isTooLate);
		} else if (isTooEarly) {
			return getCallPtaStatus(isTooEarly);
		} else if (isOnTime) {
			return getCallPtaStatus(isOnTime);
		} else if (isInProgress) {
			return getCallPtaStatus(isInProgress);
		} else if (isExecuted) {
			return getCallPtaStatus(isExecuted);
		} else if (isUnknown) {
			return getCallPtaStatus(isUnknown);
		} else {
			return getCallPtaStatus(undefined);
		}
	} else {
		const isNotOnTime = calls.find(call => getCallWindowStatus(call).type === CallWindowStatus.NOT_ON_TIME);
		const isBarelyOnTime = calls.find(call => getCallWindowStatus(call).type === CallWindowStatus.BARELY_ON_TIME);
		const isOnTime = calls.find(call => getCallWindowStatus(call).type === CallWindowStatus.ON_TIME);
		const isFinished = calls.find(call => getCallWindowStatus(call).type === CallWindowStatus.FINISHED);
		const isUnknown = calls.find(call => getCallWindowStatus(call).type === CallWindowStatus.UNKNOWN);

		if (isNotOnTime) {
			return getCallWindowStatus(isNotOnTime);
		} else if (isBarelyOnTime) {
			return getCallWindowStatus(isBarelyOnTime);
		} else if (isOnTime) {
			return getCallWindowStatus(isOnTime);
		} else if (isFinished) {
			return getCallWindowStatus(isFinished);
		} else if (isUnknown) {
			return getCallWindowStatus(isUnknown);
		} else {
			return getCallWindowStatus(undefined);
		}
	}
}

export function getCallWindowStatus(call: CallWithTimesAndActualStatusCode | null | undefined) {
	if (!call || !call.actualStatusCode) {
		return {
			color: CallStatusColor.GRAY,
			status: intl.formatMessage({ id: "UNKNOWN" }),
			type: CallWindowStatus.UNKNOWN,
			symbol: "?",
		};
	}

	switch (call.actualStatusCode) {
		case CallWindowStatus.UNKNOWN:
			return {
				color: CallStatusColor.GRAY,
				status: intl.formatMessage({ id: "UNKNOWN" }),
				type: CallWindowStatus.UNKNOWN,
				symbol: "?",

			};
		case CallWindowStatus.FINISHED:
			return {
				color: CallStatusColor.BLUE,
				status: intl.formatMessage({ id: "STATUSES_FINISHED_STATUS" }),
				type: CallWindowStatus.FINISHED,
				symbol: ">",
			};
		case CallWindowStatus.ON_TIME:
			return {
				color: CallStatusColor.GREEN,
				status: intl.formatMessage({ id: "MAP_LEGEND_MARKER_GREEN" }),
				type: CallWindowStatus.ON_TIME,
				symbol: "v",
			};
		case CallWindowStatus.BARELY_ON_TIME:
			return {
				color: CallStatusColor.ORANGE,
				status: intl.formatMessage({ id: "MAP_LEGEND_MARKER_ORANGE" }),
				type: CallWindowStatus.BARELY_ON_TIME,
				symbol: "-",
			};
		case CallWindowStatus.NOT_ON_TIME:
			return {
				color: CallStatusColor.RED,
				status: intl.formatMessage({ id: "MAP_LEGEND_MARKER_RED" }),
				type: CallWindowStatus.NOT_ON_TIME,
				symbol: "!",
			};
		default:
			return {
				color: CallStatusColor.GRAY,
				status: intl.formatMessage({ id: "UNKNOWN" }),
				type: CallWindowStatus.UNKNOWN,
				symbol: "?",
			};
	}
}

export function getCallPtaStatus(call: CallTimesFragment | null | undefined) {
	if (!call) {
		return {
			color: CallStatusColor.BLUE,
			status: intl.formatMessage({ id: "UNKNOWN" }),
			symbol: "?",
			type: CallPtaStatus.UNKNOWN,
		};
	}

	const eta = call.eta || call.systemEta;
	const ata = call.ata || call.systemAta;
	const atd = call.atd || call.systemAtd;

	if (atd) {
		return {
			color: CallStatusColor.GRAY,
			status: intl.formatMessage({ id: "CALL_STATUS_EXECUTED" }),
			symbol: ">",
			type: CallPtaStatus.EXECUTED,
		};
	} else if (eta && call.pta && moment(eta).diff(moment(call.pta), "minutes") >= 60) {
		return {
			color: CallStatusColor.RED,
			status: intl.formatMessage({ id: "CALL_STATUS_TO_LATE" }),
			symbol: "!",
			type: CallPtaStatus.TOO_LATE,
		};
	} else if (eta && call.pta && moment(call.pta).diff(moment(eta), "minutes") >= 60) {
		return {
			color: CallStatusColor.ORANGE,
			status: intl.formatMessage({ id: "CALL_STATUS_TO_EARLY" }),
			symbol: "-",
			type: CallPtaStatus.TOO_EARLY,
		};
	} else if (eta && call.pta && moment(call.pta).diff(moment(eta), "minutes") < 60 && moment(eta).diff(moment(call.pta), "minutes") < 60) {
		return {
			color: CallStatusColor.GREEN,
			status: intl.formatMessage({ id: "CALL_STATUS_ON_TIME" }),
			symbol: "v",
			type: CallPtaStatus.ON_TIME,
		};
	} else if (ata && !atd) {
		return {
			color: CallStatusColor.PURPLE,
			status: intl.formatMessage({ id: "CALL_STATUS_IN_PROGRESS" }),
			symbol: "*",
			type: CallPtaStatus.IN_PROGRESS,
		};
	} else {
		return {
			color: CallStatusColor.BLUE,
			status: intl.formatMessage({ id: "UNKNOWN" }),
			symbol: "?",
			type: CallPtaStatus.UNKNOWN,
		};
	}
}

export function getCallStatusList(session: Session) {
	if (hasPluginPermission(session, "useContainers")) {
		return [ {
			color: CallStatusColor.GRAY,
			text: intl.formatMessage({ id: "CALL_STATUS_EXECUTED" }),
		}, {
			color: CallStatusColor.RED,
			text: intl.formatMessage({ id: "CALL_STATUS_TO_LATE" }),
		}, {
			color: CallStatusColor.ORANGE,
			text: intl.formatMessage({ id: "CALL_STATUS_TO_EARLY" }),
		}, {
			color: CallStatusColor.GREEN,
			text: intl.formatMessage({ id: "CALL_STATUS_ON_TIME" }),
		}, {
			color: CallStatusColor.PURPLE,
			text: intl.formatMessage({ id: "CALL_STATUS_IN_PROGRESS" }),
		}, {
			color: CallStatusColor.BLUE,
			text: intl.formatMessage({ id: "UNKNOWN" }),
		} ];
	} else {
		return [ {
			color: CallStatusColor.GRAY,
			text: intl.formatMessage({ id: "UNKNOWN" }),
		}, {
			color: CallStatusColor.BLUE,
			text: intl.formatMessage({ id: "STATUSES_FINISHED_STATUS" }),
		}, {
			color: CallStatusColor.GREEN,
			text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_GREEN" }),
		}, {
			color: CallStatusColor.ORANGE,
			text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_ORANGE" }),
		}, {
			color: CallStatusColor.RED,
			text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_RED" }),
		} ];
	}
}

export function getCargoLengthFt(cargo?: Cargo) {
	if (cargo?.containerType) {
		return cargo.containerType.lengthFt || 0;
	} else if (cargo?.containerGroup) {
		return cargo.containerGroup.lengthFt || 0;
	} else {
		return 0;
	}
}
